/**
 * <HealthCard /> — landing-grid card for one portal per M3X.GOV.5 §4.5.
 *
 * Shows portal name (EN + AR), SandboxModeBadge, last-call timestamp,
 * accreditation status, error-rate snapshot, and click-through to the
 * portal page.
 */

import { ArrowRight, Shield, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { SandboxModeBadge } from "./SandboxModeBadge";

export type Portal = "mudad" | "gosi" | "qiwa" | "muqeem";

export interface HealthCardData {
  portal: Portal;
  mode: "sandbox" | "production";
  health_status: "green" | "amber" | "red" | "unknown";
  last_call_at: string | null;
  circuit_open: boolean;
  cool_off_remaining_s: number;
  /** Optional: when true the production-mode toggle becomes unlocked
   * in M28 admin. Driven from gov_integration_config.production_unlocked_at. */
  accredited?: boolean;
  /** Optional: 24-hour rolling error rate (0..1). */
  error_rate_24h?: number;
}

const _PORTAL_META: Record<
  Portal,
  { titleKey: string; titleFallback: string; arNameKey: string; arNameFallback: string }
> = {
  mudad: {
    titleKey: "gov.portal_mudad_title",
    titleFallback: "Mudad / WPS",
    arNameKey: "gov.portal_mudad_ar",
    arNameFallback: "نظام حماية الأجور · مدد",
  },
  gosi: {
    titleKey: "gov.portal_gosi_title",
    titleFallback: "GOSI",
    arNameKey: "gov.portal_gosi_ar",
    arNameFallback: "التأمينات الاجتماعية",
  },
  qiwa: {
    titleKey: "gov.portal_qiwa_title",
    titleFallback: "Qiwa",
    arNameKey: "gov.portal_qiwa_ar",
    arNameFallback: "قوى",
  },
  muqeem: {
    titleKey: "gov.portal_muqeem_title",
    titleFallback: "Muqeem",
    arNameKey: "gov.portal_muqeem_ar",
    arNameFallback: "مقيم",
  },
};

export function HealthCard({ card }: { card: HealthCardData }) {
  const { t, i18n } = useTranslation();
  const meta = _PORTAL_META[card.portal];
  const isAr = i18n.language?.startsWith("ar");

  return (
    <Link
      to={`/settings/integrations/gov/${card.portal}`}
      data-testid={`health-card-${card.portal}`}
      className="group flex flex-col gap-3 rounded-xl border border-border-subtle bg-surface-default p-4 hover:border-brand-blue/40 hover:shadow-sm"
    >
      <header className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-text-primary">
            {t(meta.titleKey, meta.titleFallback)}
          </h3>
          <p
            className="mt-0.5 text-xs text-text-tertiary"
            dir={isAr ? "rtl" : "ltr"}
          >
            {t(meta.arNameKey, meta.arNameFallback)}
          </p>
        </div>
        <SandboxModeBadge mode={card.mode} />
      </header>

      <dl className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <dt className="text-text-tertiary">
            {t("gov.col_last_call", "Last call")}
          </dt>
          <dd className="text-text-secondary">
            {card.last_call_at
              ? new Date(card.last_call_at)
                  .toISOString()
                  .slice(0, 16)
                  .replace("T", " ")
              : "—"}
          </dd>
        </div>
        <div>
          <dt className="text-text-tertiary">
            {t("gov.col_accreditation", "Accreditation")}
          </dt>
          <dd>
            {card.accredited ? (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase text-emerald-700">
                <Shield size={10} aria-hidden />
                {t("gov.accreditation_accredited", "Accredited")}
              </span>
            ) : card.mode === "sandbox" ? (
              <span className="text-[10px] uppercase text-amber-700">
                {t("gov.accreditation_sandbox_only", "Sandbox only")}
              </span>
            ) : (
              <span className="text-[10px] uppercase text-text-tertiary">
                {t("gov.accreditation_pending", "Pending")}
              </span>
            )}
          </dd>
        </div>
        {card.error_rate_24h !== undefined && (
          <div className="col-span-2">
            <dt className="text-text-tertiary">
              {t("gov.col_error_rate_24h", "Error rate (24h)")}
            </dt>
            <dd
              className={[
                "text-xs font-medium",
                card.error_rate_24h > 0.1
                  ? "text-rose-700"
                  : "text-emerald-700",
              ].join(" ")}
            >
              {(card.error_rate_24h * 100).toFixed(1)}%
            </dd>
          </div>
        )}
      </dl>

      {card.circuit_open && (
        <div className="flex items-center gap-1.5 rounded border border-rose-200 bg-rose-50 p-2 text-[10px] text-rose-800">
          <XCircle size={10} aria-hidden />
          {t(
            "gov.circuit_open_notice",
            "Circuit OPEN — auto-retry in {{seconds}}s",
            { seconds: Math.round(card.cool_off_remaining_s) },
          )}
        </div>
      )}

      <div className="mt-1 flex items-center justify-end text-[11px] text-brand-blue group-hover:text-brand-deep">
        {t("gov.open_portal", "Open")}
        <ArrowRight size={11} aria-hidden className="ms-1 rtl:rotate-180" />
      </div>
    </Link>
  );
}

export default HealthCard;
